import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPMessageBar, IPMessageBarType, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import { getErrorMessageText } from 'components/Notification/ErrorMessageService';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import UserPicker from 'components/Pickers/UserPicker';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import {
  AssessmentAutomations,
  AssessmentTaskCreate,
  AssessmentTaskCreated,
  AssessmentTaskRefusal,
} from 'models/auditCriteriaGenericSets';
import { AuditCriteriaLayer } from 'models/auditCriteriaGeneric';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiCreateAssessmentTask,
  apiCreateAssessmentTasks,
  apiGetAssessmentAutomations,
  apiGetAuditCriteria,
  apiRunAssessmentEvidenceAudit,
} from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import ControllerException, { ControllerExceptionCodes } from 'utils/controllerException';
import { addDateDays } from 'utils/datetime';
import { getEvidenceAuditRefusalText } from './evidenceAuditTexts';

interface IAssessmentStartCardProps {
  entityType: number;
  // One item, or the selected rows of a list (one task per item).
  entityIds: number[];
  // The items' names, for the title and the result per item.
  entityNames?: Map<number, string>;
  // Assessment tasks created elsewhere (the asset inquiry's completion): the card asks whether the AI evidence audit
  // runs for them, one run per task; without an audit to offer it only tells that the tasks were created.
  occurrenceTasks?: AssessmentTaskCreated[];
  onClose: () => void;
  // The tasks were created (or their audits were asked to run).
  onDone?: (created: AssessmentTaskCreated[]) => void;
}

/**
 * The assessment start card (PLAN-075 spec 5.8, simplified by PLAN-CTI-090): "Assess" on an item or on the selected
 * rows of a list. It asks the assignee, the start and the deadline, and whether the AI evidence audit (PLAN-077) runs,
 * ticked beforehand; the server starts that audit in the background with the new task as its consumer. After the
 * create it shows per item what was refused and whether the audit started. For tasks created elsewhere (the asset
 * inquiry) the card is only the audit question.
 */
const AssessmentStartCard = (props: IAssessmentStartCardProps) => {
  const { t } = useTranslation(['criteria', 'translation', 'exceptions']);
  const appContext = useContext(AppContext);
  const isOccurrence = props.occurrenceTasks !== undefined;

  const [automations, setAutomations] = useState<AssessmentAutomations | undefined>(undefined);
  // the AI evidence audit runs unless the person unticks it (PLAN-077 decision 7)
  const [isEvidenceAuditChosen, setIsEvidenceAuditChosen] = useState<boolean>(true);
  // Chris's ruling: the task starts today and is due in two weeks unless the user picks other dates
  const [startDate, setStartDate] = useState<Date | undefined>(() => new Date());
  const [deadline, setDeadline] = useState<Date | undefined>(() => addDateDays(new Date(), 14));
  const [assigneeId, setAssigneeId] = useState<string | undefined>(undefined);
  // who did the item's latest assessment: the assignee starts as that person (one item only)
  const [latestAssigneeId, setLatestAssigneeId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // after the create: each item's task with the audit's line, and the refused items
  const [created, setCreated] = useState<AssessmentTaskCreated[] | undefined>(undefined);
  const [refused, setRefused] = useState<AssessmentTaskRefusal[]>([]);
  // one item that resolves to no criteria set: there is nothing to assess, so the create is not offered (AUDIT0039)
  const [noCriteriaSet, setNoCriteriaSet] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        if (!isOccurrence && props.entityIds.length === 1) {
          const set = await apiGetAuditCriteria(props.entityType, props.entityIds[0], accessToken);
          if (set.layer === AuditCriteriaLayer.None && !set.hasCriteria) {
            setNoCriteriaSet(true);

            return;
          }
        }
        const loaded = await apiGetAssessmentAutomations(props.entityType, props.entityIds, accessToken);
        setAutomations(loaded);
        if (!isOccurrence && loaded.latestAssigneeId) {
          setLatestAssigneeId(loaded.latestAssigneeId);
          setAssigneeId((current) => current ?? loaded.latestAssigneeId);
        }
      } catch (err) {
        if (AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_InternalAudit_NoCriteriaSet) {
          setNoCriteriaSet(true);
        } else {
          appContext.setError(err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.entityType, props.entityIds.join(',')]);

  const isEvidenceAuditOffered = automations?.evidenceAudit?.offered === true;

  const submit = async () => {
    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let result: AssessmentTaskCreated[];
      let refusals: AssessmentTaskRefusal[] = [];

      if (props.occurrenceTasks) {
        // one run per task: a refused run is told on its item and does not stop the others
        result = [];
        for (const task of props.occurrenceTasks) {
          try {
            result.push(await apiRunAssessmentEvidenceAudit(task.taskId, accessToken));
          } catch (err) {
            const refusal = new AssessmentTaskRefusal();
            refusal.entityType = task.entityType;
            refusal.entityId = task.entityId;
            refusal.message =
              err instanceof AppError ? getErrorMessageText(err, t) : t('exceptions:General.UnknownError');
            refusals.push(refusal);
          }
        }
      } else {
        const request = new AssessmentTaskCreate();
        request.entityType = props.entityType;
        request.entityIds = [...props.entityIds];
        request.startDate = startDate;
        request.deadline = deadline;
        request.assigneeId = assigneeId;
        request.runEvidenceAudit = isEvidenceAuditOffered && isEvidenceAuditChosen;

        const outcome =
          props.entityIds.length === 1
            ? await apiCreateAssessmentTask(props.entityType, props.entityIds[0], request, accessToken)
            : await apiCreateAssessmentTasks(request, accessToken);
        result = outcome.created;
        refusals = outcome.refused;
      }

      if (result.length > 0 && !isOccurrence) {
        appContext.showNotification(t('criteria:Generic.Start.Created', { count: result.length }), false, false, true);
      }
      props.onDone?.(result);

      // the evidence audit's line (runs in the background, or why not) is told per item, so the card stays open for it
      if (refusals.length === 0 && !result.some((item) => item.evidenceAudit !== undefined)) {
        props.onClose();

        return;
      }

      setRefused(refusals);
      setCreated(result);
    } catch (err) {
      // a refusal the server explains (no criteria set, no permission) is told under the dialog; the rest is an error
      if (err instanceof AppError && AppError.isControllerException(err)) {
        appContext.showNotification(getErrorMessageText(err, t), true, false, true);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const getItemName = (entityId: number): string => props.entityNames?.get(entityId) ?? `#${entityId}`;

  const renderResult = () => (
    <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 12 } }}>
      {refused.map((item) => (
        <IPStack.Item key={`refused-${item.entityId}`}>
          <IPMessageBar messageBarType={IPMessageBarType.warning}>
            {t('criteria:Generic.Start.Refused', {
              name: getItemName(item.entityId),
              message: item.code
                ? ControllerException.getMessage(item.code as ControllerExceptionCodes, t, item.message)
                : item.message,
            })}
          </IPMessageBar>
        </IPStack.Item>
      ))}
      {(created ?? []).map((item) => (
        <IPStack.Item key={`created-${item.taskId}`}>
          {(created ?? []).length > 1 && <IPLabel>{getItemName(item.entityId)}</IPLabel>}
          <IPStack tokens={globalStackTokensGapSmall}>
            {item.evidenceAudit && (
              <IPText variant={item.evidenceAudit.started ? undefined : 'small'}>
                {item.evidenceAudit.started
                  ? t('criteria:Generic.Start.EvidenceAuditStarted')
                  : t('criteria:Generic.Start.EvidenceAuditRefused', {
                      reason: getEvidenceAuditRefusalText(item.evidenceAudit.reason, t),
                    })}
              </IPText>
            )}
          </IPStack>
        </IPStack.Item>
      ))}
    </IPStack>
  );

  const renderChoices = () => (
    <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 12 } }}>
      {noCriteriaSet && (
        <IPStack.Item>
          <IPMessageBar messageBarType={IPMessageBarType.warning}>{t('exceptions:Audit.NoCriteriaSet')}</IPMessageBar>
        </IPStack.Item>
      )}
      {!isOccurrence && !noCriteriaSet && (
        <>
          <IPStack.Item>
            <IPLabel>{t('criteria:Generic.Assess.Assignee')}</IPLabel>
            <UserPicker
              users={appContext.globalDataCache.users.items}
              selectedItemId={assigneeId}
              onSelect={(user) => setAssigneeId(user?.id)}
            />
            <IPText variant="small">
              {latestAssigneeId && assigneeId === latestAssigneeId
                ? t('criteria:Generic.Assess.AssigneeInfoLatest')
                : t('criteria:Generic.Assess.AssigneeInfo')}
            </IPText>
          </IPStack.Item>
          <IPStack.Item>
            <LocalizedDatePicker
              label={t('criteria:Generic.Assess.StartDate')}
              showGoToToday={true}
              value={startDate}
              onDateChange={(value) => setStartDate(value)}
            />
          </IPStack.Item>
          <IPStack.Item>
            <LocalizedDatePicker
              label={t('criteria:Generic.Assess.Deadline')}
              showGoToToday={true}
              minDate={startDate ?? new Date()}
              value={deadline}
              onDateChange={(value) => setDeadline(value)}
            />
          </IPStack.Item>
        </>
      )}
      {isLoading && (
        <IPStack.Item>
          <IPSpinner
            size={IPSpinnerSize.small}
            label={t('translation:General.Notifications.Loading')}
            labelPosition="right"
          />
        </IPStack.Item>
      )}
      {isEvidenceAuditOffered && (
        <IPStack.Item>
          <IPSeparator />
          <IPCheckbox
            label={t('criteria:Generic.Start.EvidenceAudit', { count: automations?.evidenceAudit?.criteriaCount ?? 0 })}
            checked={isEvidenceAuditChosen}
            disabled={isSaving}
            onChange={(_, checked) => setIsEvidenceAuditChosen(checked === true)}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );

  const getTitle = (): string => {
    const base = props.occurrenceTasks
      ? t('criteria:Generic.Start.OccurrenceBatchTitle', { count: props.occurrenceTasks.length })
      : t('criteria:Generic.Assess.Title');
    if (props.entityIds.length !== 1) return base;

    const name = props.entityNames?.get(props.entityIds[0]);

    return name ? `${base} - ${name}` : base;
  };

  // the card of tasks created elsewhere is only the audit question: without an audit to offer it does not show, and it
  // waits for the read; the tasks are then only told
  useEffect(() => {
    if (!isOccurrence || isLoading || isEvidenceAuditOffered) return;

    if (props.occurrenceTasks && props.occurrenceTasks.length > 0) {
      appContext.showNotification(
        t('criteria:Generic.Start.Created', { count: props.occurrenceTasks.length }),
        false,
        false,
        true,
      );
    }
    props.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOccurrence, isLoading, isEvidenceAuditOffered]);

  if (isOccurrence && (isLoading || !isEvidenceAuditOffered)) return null;

  return (
    <GenericModal
      isOpen={true}
      isBlocking
      onClose={props.onClose}
      title={getTitle()}
      subTitle={
        created
          ? undefined
          : isOccurrence
          ? t('criteria:Generic.Start.OccurrenceSubTitle')
          : props.entityIds.length > 1
          ? t('criteria:Generic.Start.BatchSubTitle', { count: props.entityIds.length })
          : t('criteria:Generic.Assess.SubTitle')
      }
      hideFooter={false}
      isActionPending={isSaving || isLoading}
      width={620}
      maxWidth={'min(90vw, 620px)'}
      maxHeight={'85vh'}
      {...(created
        ? { closeButtonText: t('criteria:Close') }
        : {
            saveButtonText: isOccurrence
              ? t('criteria:Generic.Start.OccurrenceRun')
              : props.entityIds.length > 1
              ? t('criteria:Generic.Assess.CreateMany')
              : t('criteria:Generic.Assess.Create'),
            closeButtonText: isOccurrence
              ? t('criteria:Generic.Start.OccurrenceNotNow')
              : t('translation:General.Button.Cancel'),
            // the audit question of tasks created elsewhere runs nothing when the audit is unticked
            saveDisabled: isLoading || noCriteriaSet || (isOccurrence && !isEvidenceAuditChosen),
            keepOpenAfterSave: true,
            onSave: () => submit(),
          })}
    >
      {created ? renderResult() : renderChoices()}
    </GenericModal>
  );
};

export default AssessmentStartCard;
